import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge, Card, CardContent } from '@/components/ui';
import type { IqServiceDetail, IqServiceStatus } from '@/types/iq-service';

interface IqServiceListProps {
  services: IqServiceDetail[];
}

const statusVariant: Record<IqServiceStatus, 'success' | 'warning' | 'destructive'> = {
  active: 'success',
  inactive: 'warning',
  suspended: 'destructive',
};

/**
 * IQ-Service card list. Click navigates to detail page.
 */
export function IqServiceList({ services }: IqServiceListProps) {
  const { t } = useTranslation('iq-services');
  const navigate = useNavigate();

  if (services.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          {t('list.empty')}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-2">
      {services.map((service) => (
        <Card
          key={service.iqServiceId}
          className="cursor-pointer transition-colors hover:bg-accent/50"
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/iq-services/${service.iqServiceId}`)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/iq-services/${service.iqServiceId}`); } }}
        >
          <CardContent className="p-4">
            <div className="flex w-full items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium truncate">{service.displayName}</span>
                  <Badge variant={statusVariant[service.status]}>
                    {t(`status.${service.status}`)}
                  </Badge>
                  <Badge variant="outline">
                    {t(`gatewayMode.${service.gatewayMode}`)}
                  </Badge>
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {service.merchantName}
                  {' · '}
                  {(service.assignedServiceCount ?? 0) > 0
                    ? t('list.assignedServices', { count: service.assignedServiceCount })
                    : t('list.noAssignedServices')}
                </div>
              </div>
              <div className="text-xs font-mono text-muted-foreground shrink-0">
                {service.iqServiceId}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
